import { css } from '@emotion/react';
import { type FC } from 'react';
import {
  useDrag, useDrop,
} from 'react-dnd';
import { DndType } from '~/_shared/constants/dndType.enum';
import type { Theme } from '~/_shared/themes/theme.model';
import { type SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import { compareSpreadsheetColumnIds } from '~/_shared/types/spreadsheetData/spreadsheetColumns.helpers';
import {
  type BubbleDndItem, type BubbleEditorItem,
} from '../locationData.types';
import { type ColumnStyleSettingsData } from '../panelSettings.types';
import { BubbleItemWithIconContainer } from './bubbleItemWithIcon.container';
import {
  areDnDTypesEqual, getItemDnDType,
} from './draggableItem.helpers';

const wrapperStyle = ({ isSelected, invalidTarget }: { isSelected?: boolean; invalidTarget: boolean }) => (theme: Theme) => css({
  backgroundColor: isSelected ? theme.backgroundColors.secondaryHover : '',
  cursor: invalidTarget ? 'not-allowed' : 'default',
});

type DraggableItemProps = Readonly<{
  className?: string;
  item: BubbleEditorItem;
  isDraggable: boolean;
  isVisible: boolean;
  onVisibilityIconClick: () => void;
  showVisibilityIcon: boolean;
  isSelected?: boolean;
  bulkStyles: ColumnStyleSettingsData;

  onDrop: (sourceId: SpreadsheetColumnId, targetId: SpreadsheetColumnId) => void;
}>;

export const DraggableItemComponent: FC<DraggableItemProps> = ({
  bulkStyles,
  className,
  isDraggable,
  isSelected,
  isVisible,
  item,
  onDrop,
  onVisibilityIconClick,
  showVisibilityIcon,
  ...restProps
}) => {
  const itemType = getItemDnDType(item.itemType);
  const [, drag] = useDrag({
    type: itemType,
    item,
    canDrag: isDraggable,
  });
  const [{ isOver, canDrop }, drop] = useDrop<
    { type: string; item: BubbleEditorItem },
    void,
    { isOver: boolean; canDrop: boolean }
  >({
    accept: [DndType.LocalizationBasicItem, DndType.LocalizationContactItem],
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: areDnDTypesEqual(monitor.getItemType(), itemType),
    }),
    hover: (draggedItem: BubbleDndItem, monitor) => {
      if (areDnDTypesEqual(monitor.getItemType(), itemType) && !compareSpreadsheetColumnIds(draggedItem.id, item.id)) {
        onDrop(draggedItem.id, item.id);
      }
    },
  });

  return (
    <div
      {...restProps}
      className={className}
      css={wrapperStyle({ isSelected, invalidTarget: isOver && !canDrop })}
      ref={el => {
        drag(el);
        drop(el);
      }}
    >
      <BubbleItemWithIconContainer
        showDragIcon={isDraggable}
        item={item}
        isDraggable={isDraggable}
        isItemVisible={isVisible}
        onVisibilityIconClick={onVisibilityIconClick}
        showVisibilityIcon={showVisibilityIcon}
        bulkStyles={bulkStyles}
      />
    </div>
  );
};
